// Hook for managing frames with selection, filtering, and batch operations
import { useState, useCallback, useMemo, useEffect } from 'react'
import { 
  CapturedFrame, 
  FrameFilterOptions, 
  framesStorageService 
} from './frames-store'
import { LOCAL_LIBRARY_ID } from '@/lib/browser-persistence'
import { track } from '@/lib/track'

export interface UseFramesOptions {
  userId?: string
  videoId?: string
  autoLoad?: boolean
}

export interface FrameSelection {
  selectedIds: Set<string>
  isAllSelected: boolean
  toggleSelection: (frameId: string) => void
  selectAll: () => void
  selectNone: () => void
  selectFiltered: () => void
  isSelected: (frameId: string) => boolean
  selectedCount: number
}

export interface FrameActions {
  deleteSelected: () => Promise<void>
  deleteFrame: (frameId: string) => Promise<void>
  downloadSelected: () => Promise<void>
  downloadFrame: (frame: CapturedFrame) => void
  refresh: () => Promise<void>
}

export function useFrames({ userId, videoId, autoLoad = true }: UseFramesOptions) {
  const [frames, setFrames] = useState<CapturedFrame[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [filterOptions, setFilterOptions] = useState<FrameFilterOptions>({
    sortBy: 'frameNumber',
    sortOrder: 'asc'
  })
  const [highlightedFrameId, setHighlightedFrameId] = useState<string | null>(null)

  // Load frames
  const loadFrames = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const options: FrameFilterOptions = {
        ...filterOptions,
        videoId: videoId || filterOptions.videoId
      }
      const loadedFrames = await framesStorageService.filterFrames(userId || LOCAL_LIBRARY_ID, options)
      setFrames(loadedFrames)
    } catch (err) {
      console.error('Failed to load frames:', err)
      setError(err instanceof Error ? err.message : 'Failed to load frames')
    } finally {
      setIsLoading(false)
    }
  }, [userId, videoId, filterOptions])

  // Auto-load on mount and when dependencies change
  useEffect(() => {
    if (autoLoad) {
      loadFrames()
    }
  }, [autoLoad, userId, loadFrames])

  // Filtered frames based on current filter options
  const filteredFrames = useMemo(() => {
    // Frames are already filtered from the service, but we can apply additional client-side filtering
    return frames
  }, [frames])

  // Captured frame numbers for timeline markers
  const capturedFrameNumbers = useMemo(() => {
    return new Set(frames.map(f => f.frameNumber))
  }, [frames])

  // Selection management
  const selection: FrameSelection = useMemo(() => {
    const isAllSelected = filteredFrames.length > 0 && 
      filteredFrames.every(f => selectedIds.has(f.id))

    return {
      selectedIds,
      isAllSelected,
      toggleSelection: (frameId: string) => {
        setSelectedIds(prev => {
          const next = new Set(prev)
          if (next.has(frameId)) {
            next.delete(frameId)
          } else {
            next.add(frameId)
          }
          return next
        })
      },
      selectAll: () => {
        setSelectedIds(new Set(filteredFrames.map(f => f.id)))
      },
      selectNone: () => {
        setSelectedIds(new Set())
      },
      selectFiltered: () => {
        setSelectedIds(new Set(filteredFrames.map(f => f.id)))
      },
      isSelected: (frameId: string) => selectedIds.has(frameId),
      selectedCount: selectedIds.size
    }
  }, [selectedIds, filteredFrames])

  // Get selected frames
  const selectedFrames = useMemo(() => {
    return filteredFrames.filter(f => selectedIds.has(f.id))
  }, [filteredFrames, selectedIds])

  // Frame actions
  const actions: FrameActions = useMemo(() => ({
    deleteSelected: async () => {
      if (selectedIds.size === 0) return

      try {
        const ids = Array.from(selectedIds)
        await framesStorageService.deleteFrames(ids)
        setSelectedIds(new Set())
        await loadFrames()
      } catch (err) {
        console.error('Failed to delete frames:', err)
        throw err
      }
    },

    deleteFrame: async (frameId: string) => {
      try {
        await framesStorageService.deleteFrame(frameId)
        setSelectedIds(prev => {
          const next = new Set(prev)
          next.delete(frameId)
          return next
        })
        await loadFrames()
      } catch (err) {
        console.error('Failed to delete frame:', err)
        throw err
      }
    },
    
    downloadSelected: async () => {
      if (selectedIds.size === 0) return

      const framesToDownload = filteredFrames.filter(f => selectedIds.has(f.id))
      
      if (framesToDownload.length === 1) {
        // Single file download
        downloadSingleFrame(framesToDownload[0])
      } else {
        // Multiple files - create ZIP
        await downloadFramesAsZip(framesToDownload)
      }
    },
    
    downloadFrame: (frame: CapturedFrame) => {
      downloadSingleFrame(frame)
    },

    refresh: loadFrames,
  }), [selectedIds, filteredFrames, loadFrames])

  // Update filter options
  const updateFilter = useCallback((options: Partial<FrameFilterOptions>) => {
    setFilterOptions(prev => ({ ...prev, ...options }))
  }, [])

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilterOptions({
      sortBy: 'frameNumber',
      sortOrder: 'asc'
    })
  }, [])

  // Get frame by frame number
  const getFrameByNumber = useCallback((frameNumber: number) => {
    return frames.find(f => f.frameNumber === frameNumber)
  }, [frames])

  // Highlight a frame (for timeline interaction)
  const highlightFrame = useCallback((frameId: string | null) => {
    setHighlightedFrameId(frameId)
  }, [])

  return {
    frames: filteredFrames,
    isLoading,
    error,
    loadFrames,
    selection,
    selectedFrames,
    actions,
    filterOptions,
    updateFilter,
    clearFilters,
    capturedFrameNumbers,
    getFrameByNumber,
    highlightedFrameId,
    highlightFrame
  }
}

// Helper to download a single frame
function downloadSingleFrame(frame: CapturedFrame) {
  const link = document.createElement('a')
  link.href = frame.imageData
  link.download = formatFrameFilename(frame.frameNumber, frame.videoName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  track('exported', 'frame', frame.fileSize)
}

// Helper to download multiple frames as ZIP
async function downloadFramesAsZip(frames: CapturedFrame[]) {
  // Dynamically import JSZip
  const JSZip = (await import('jszip')).default
  const zip = new JSZip()

  // Add each frame to the ZIP
  for (const frame of frames) {
    const filename = formatFrameFilename(frame.frameNumber, frame.videoName)
    // Remove data URL prefix to get raw base64
    const base64Data = frame.imageData.split(',')[1]
    zip.file(filename, base64Data, { base64: true })
  }

  // Generate the ZIP file
  const content = await zip.generateAsync({ type: 'blob' })

  // Download the ZIP
  const url = URL.createObjectURL(content)
  const link = document.createElement('a')
  link.href = url
  
  // Generate ZIP filename
  const videoName = frames[0]?.videoName?.replace(/\.[^/.]+$/, '') || 'frames'
  link.download = `${videoName}_frames_${frames.length}.zip`
  
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
  track('exported', 'zip', frames.length)
}

// Format frame filename (duplicated from utils to avoid circular deps)
function formatFrameFilename(frameNumber: number, videoName?: string | null): string {
  const frameStr = frameNumber.toString().padStart(6, '0')
  
  if (videoName) {
    const baseName = videoName.replace(/\.[^/.]+$/, '')
    const sanitizedName = baseName.replace(/[^a-zA-Z0-9_-]/g, '_')
    return `${sanitizedName}__${frameStr}.png`
  }
  
  return `frame_${frameStr}.png`
}
